import React, { useEffect, useState } from "react";
import Modal from "../../../../utils/components/Modal";
import Input from "../../../../utils/components/Input";
import SelectBox from "../../../../utils/components/SelectBox";
import inputValidations from "../../../../utils/helpers/inputValidations";
import toastr from "toastr";
import API from "../../../../utils/API";
import COMPANY_USER_ROLES from "../../../../utils/db/companyUserRoles";
import availableLanguages from "../../../../utils/db/availableLanguages";
import { useParams } from "react-router";
import { useUser } from "../../../../utils/hooks/useUser";
import { useSelector } from "react-redux";
import fillTemplate from "../../../../utils/helpers/fillTemplate";
import {
  allowedRoleOptions,
  editableUserFields,
  isProjectAdmin,
  pickEditablePayload,
  ROLE_PROJECT_ADMIN,
} from "../../../../utils/roles";

const NewCompanyUserModal = ({
  onClose,
  selectedUserId,
  projectId,
  companyId,
  groups,
  show,
  refreshData,
  translate,
  userRoles,
  setUserRoles,
  companyProjects,
}) => {
  const [user, setUser] = React.useState({});
  const { id: company_id } = useParams();
  const [holdButton, setHoldButton] = useState(true);
  // Fixed when the user loads; picking a project in the form must not unlock role and groups
  const [sharesProject, setSharesProject] = useState(false);

  const authUser = useUser();
  const memberProjects = useSelector((state) => state.app.projects) || [];
  const authIsProjectAdmin = isProjectAdmin(authUser);
  const targetUser = selectedUserId && user.id ? user : null;
  const editableFields = editableUserFields(
    authUser,
    targetUser,
    sharesProject
  );
  const canEdit = (field) => editableFields.includes(field);
  const projectOptions = authIsProjectAdmin ? memberProjects : companyProjects;
  const projectAdminSelected =
    authUser?.role?.name === "admin" &&
    userRoles.find((role) => role.id === user?.role?.id)?.name ===
      ROLE_PROJECT_ADMIN;
  const showNarrowingWarning =
    projectAdminSelected && user?.application_role?.name === "manager";
  const selectedProjectNames = (user?.projects || []).map(
    (project) => project.label
  );

  useEffect(() => {
    if (show && !userRoles.length) {
      API.getData("application_roles").then(({ data: res }) => {
        setUserRoles(res.data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const getUser = () => {
    let url = "user/" + selectedUserId;
    API.getData(url).then(({ data: res }) => {
      const selectedGroups = res.data.groups?.map((group) => ({
        value: group.id.toString(),
        label: group.name,
      }));

      let selectedRole = COMPANY_USER_ROLES.find(
        (role) => role.id === parseInt(res.data.application_role.id)
      );

      const selectedProjects =
        res.data.projects?.map((project) => ({
          value: project.id.toString(),
          label: project.name,
        })) || [];

      if (!selectedRole) {
        selectedRole = "";
      }

      const userRole = {
        id: selectedRole.id,
        value: String(selectedRole.id),
        label: selectedRole
          ? translate(`user.roles.${selectedRole.display_name}`)
          : "",
      };

      setTimeout(() => {
        setSharesProject(selectedProjects.length > 0);
        setUser({
          ...res.data,
          groups: selectedGroups,
          role: userRole,
          projects: selectedProjects,
          lang: res.data.lang || 'tr',
        });
      }, 50);
    });
  };

  const handleChange = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  const updateUser = (payload) => {
    setHoldButton(false);
    if (canEdit("password") && user.password && user.password_confirmation) {
      if (user.password !== user.password_confirmation) {
        toastr.error(translate("auth.reset_password.password_not_match"));
        setHoldButton(true);
        return;
      }

      if (!inputValidations(user.password, "password")) {
        toastr.error(translate("auth.reset_password.invalid_password"));
        setHoldButton(true);
        return;
      }

      payload.password = user.password;
    }

    API.updateUserData(payload, selectedUserId)
      .then(({ data: res }) => {
        if (res.data.name) {
          toastr.success("User updated successfully");
          onClose();

          refreshData();

          setHoldButton(false);

          setTimeout(resetForm, 50);
        }
      })
      .catch((res) => {
        setHoldButton(true);
      });
  };

  const createUser = (payload) => {
    setHoldButton(false);
    if (!user.password) {
      toastr.error(translate("company.users.rules.required-password"));
      setHoldButton(true);
      return;
    }

    if (user.password !== user.password_confirmation) {
      toastr.error(translate("company.users.rules.match-password"));
      setHoldButton(true);
      return;
    }

    if (!inputValidations(user.password, "password")) {
      toastr.error(translate("company.users.rules.required-password"));
      setHoldButton(true);
      return;
    }

    API.createUser(projectId, companyId, payload)
      .then(({ data: res }) => {
        if (res.data.name) {
          toastr.success("User created successfully");
          refreshData();
          onClose();

          setHoldButton(false);

          setTimeout(resetForm, 50);
        }
      })
      .catch(function (error) {
        setHoldButton(true);
      });
  };

  useEffect(() => {
    if (show) {
      setHoldButton(true);
    }
  }, [show]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const required = ["name", "email", "role"].filter(canEdit);

    const check = required.some((field) => {
      toastr.clear();
      if (!user[field]) {
        toastr.error(translate("company.users.rules.required-" + field));
        return true;
      }

      if (field === "email" && !inputValidations(user.email, "email")) {
        toastr.error(translate("company.users.rules.format-email"));
        return true;
      }

      return false;
    });

    if (check) {
      return;
    }

    const payload = {
      email: user.email,
      name: user.name,
      application_role_id: user?.role?.id,
      title: user.title,
      groups:
        user?.role?.id !== 1 ? user?.groups?.map((group) => group.value) : [],
      company_id: parseInt(company_id),
      projects: user?.projects?.map((project) => parseInt(project.value)),
      lang: user.lang || "tr",
    };

    if (selectedUserId) {
      updateUser(
        authIsProjectAdmin ? pickEditablePayload(payload, editableFields) : payload
      );
    } else {
      payload.password = user.password;
      createUser(payload);
    }
  };

  const resetForm = () => {
    setSharesProject(false);
    setUser({
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      title: "",
      application_role: null,
      groups: [],
      lang: "tr",
    });
  };

  React.useEffect(() => {
    if (selectedUserId) {
      getUser();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  return (
    <div>
      <Modal
        title={translate(
          selectedUserId
            ? `user.operations.edit-user`
            : `user.operations.new-user`
        )}
        positiveButtonNode={translate("save")}
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={handleSubmit}
        negativeButtonHandler={() => {
          onClose();
          setTimeout(resetForm, 50);
        }}
        setShow={() => {
          onClose();
          setTimeout(resetForm, 50);
        }}
        modalSize="xl"
        show={show}
        disableButtons={!holdButton}
      >
        <div className="company-user-form">
          <div className="d-flex flex-wrap align-items-center">
            {canEdit("name") && (
              <div className="col-12 col-md-6 mt-2 pe-3">
                <Input
                  type="text"
                  title={translate("company.add-user-modal.name-and-surname")}
                  value={user.name}
                  name="name"
                  onChange={handleChange}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.name-and-surname-input"
                  )}
                />
              </div>
            )}
            {canEdit("email") && (
              <div className="col-12 col-md-6 mt-2 pe-3">
                <Input
                  type="email"
                  title={translate("company.add-user-modal.email")}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.email-input"
                  )}
                  name="email"
                  onChange={handleChange}
                  value={user.email}
                />
              </div>
            )}
            {canEdit("password") && (
              <div className="col-12 col-md-6 mt-4 pe-3">
                <Input
                  type="password"
                  title={translate("company.add-user-modal.password")}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.password-input"
                  )}
                  name="password"
                  onChange={handleChange}
                  value={user.password}
                  inputProps={{
                    autoComplete: "new-password",
                  }}
                />
              </div>
            )}
            {canEdit("password_confirmation") && (
              <div className="col-12 col-md-6 mt-4 pe-3">
                <Input
                  type="password"
                  title={translate("company.add-user-modal.password-confirm")}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.password-confirm-input"
                  )}
                  name="password_confirmation"
                  onChange={handleChange}
                  value={user.password_confirmation}
                />
              </div>
            )}
            {canEdit("title") && (
              <div className="col-12 col-md-6 mt-4 pe-3">
                <Input
                  type="text"
                  title={translate("company.add-user-modal.title")}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.title-input"
                  )}
                  name="title"
                  onChange={handleChange}
                  value={user.title}
                />
              </div>
            )}
            {canEdit("role") && (
              <div className="col-12 col-md-6 mt-4 pe-3">
                <label className="form-label fw-bold pb-0">
                  {translate("company.add-user-modal.role")}
                </label>
                <SelectBox
                  options={allowedRoleOptions(authUser?.role, userRoles)
                    .sort((a, b) => a.id - b.id)
                    .map((role) => ({
                      id: role.id,
                      value: String(role.id),
                      label: translate(`user.roles.${role.name.toLowerCase()}`),
                    }))}
                  value={user.role}
                  onChange={(value) => setUser({ ...user, role: value })}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.select-role"
                  )}
                />
              </div>
            )}
            {projectAdminSelected && (
              <div className="col-12 mt-2 pe-3">
                <div className="alert alert-warning mb-0">
                  <div>
                    {fillTemplate(
                      translate(
                        selectedProjectNames.length
                          ? "user.project_admin_role_info"
                          : "user.project_admin_role_info_no_projects",
                        { count: selectedProjectNames.length }
                      ),
                      {
                        name: <b>{user?.name || translate("user.this_user")}</b>,
                        projects: selectedProjectNames.join(", "),
                      }
                    )}
                  </div>
                  {showNarrowingWarning && (
                    <div className="mt-1">
                      {translate("user.project_admin_narrowing_warning")}
                    </div>
                  )}
                </div>
              </div>
            )}
            {canEdit("lang") && (
              <div className="col-12 mt-4 pe-3">
                <label className="form-label fw-bold pb-0">
                  {translate("company.add-user-modal.language")}
                </label>
                <SelectBox
                  options={availableLanguages.map((lang) => ({
                    value: lang.code,
                    label: lang.name,
                  }))}
                  value={
                    availableLanguages
                      .filter((l) => l.code === (user.lang || "tr"))
                      .map((l) => ({ value: l.code, label: l.name }))[0] || null
                  }
                  onChange={(item) => setUser({ ...user, lang: item?.value })}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.select-language"
                  )}
                />
              </div>
            )}
            {user?.role?.id !== 1 && canEdit("groups") && (
              <div className="col-12 mt-4 pe-3">
                <label className="form-label fw-bold pb-0">
                  {translate("company.add-user-modal.groups")}
                </label>
                <SelectBox
                  options={groups.map((group) => ({
                    id: group.id,
                    value: group.id.toString(),
                    label: group.name,
                  }))}
                  value={user.groups}
                  isMulti={true}
                  onChange={(groups) => {
                    setUser({ ...user, groups });
                  }}
                  placeholder={translate(
                    "company.add-user-modal.place-holders.select-groups"
                  )}
                />
              </div>
            )}
            {canEdit("projects") && (
              <div className="col-12 mt-4 pe-3">
                <label className="form-label fw-bold pb-0">
                  {translate("projects")}
                </label>
                <SelectBox
                  options={
                    projectOptions?.map((project) => ({
                      id: project.id,
                      value: project.id.toString(),
                      label: project.name,
                    })) || []
                  }
                  value={user.projects}
                  isMulti={true}
                  onChange={(projects) => {
                    setUser({ ...user, projects });
                  }}
                  placeholder={translate("projects")}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NewCompanyUserModal;
